/**
 * GOMEZ / FM theme shell scripts.
 *
 * Loaded once, deferred, from layout/theme.liquid. Most pieces below are
 * self-contained IIFEs reading a small HTML/data-attribute contract, with
 * no global footprint. Two exceptions, declared as real globals on
 * purpose: `gomezFetchSections` and `gomezRefreshCartChrome` (below) are
 * shared by this file's own cart-drawer AJAX controller *and* by
 * assets/product.js and assets/cart.js, which load after this file on
 * their respective routes — extracting the fetch/parse/replace logic
 * here once, instead of duplicating it per route, is exactly what those
 * two route-scoped files rely on.
 *
 * Everything here is progressive enhancement: the markup it enhances
 * (forms, <details>, <dialog>) already works without JavaScript.
 */

/**
 * Safely parses a `<script type="application/json">` element's content.
 * Shared by this file and assets/product.js (which defines its own
 * product-specific strings object using this same reader).
 */
function gomezReadJSON(script) {
  if (!script) {
    return null;
  }
  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    return null;
  }
}

/**
 * Theme-wide strings JS can't produce itself (translated text). Rendered
 * once by layout/theme.liquid as `<script type="application/json"
 * data-theme-strings>`, available on every route.
 */
var gomezThemeStrings = gomezReadJSON(document.querySelector('[data-theme-strings]')) || {};

/**
 * Shared Section Rendering API helper.
 *
 * Fetches one or more sections via `?sections=id,id` and resolves to
 * `{ id: parsedDocument }` for each — callers do their own targeted DOM
 * replacement rather than this helper guessing what to swap, since
 * different callers need different elements out of the same section
 * (e.g. the `header` section contains both the cart drawer and every
 * `[data-cart-count]` badge).
 */
function gomezFetchSections(sectionIds) {
  var url = new URL(window.location.origin + window.location.pathname);
  url.searchParams.set('sections', sectionIds.join(','));

  return fetch(url.toString())
    .then(function (response) {
      if (!response.ok) {
        throw new Error('Section refresh failed');
      }
      return response.json();
    })
    .then(function (data) {
      var parser = new DOMParser();
      var docs = {};
      sectionIds.forEach(function (id) {
        var html = data && data[id];
        docs[id] = html ? parser.parseFromString(html, 'text/html') : null;
      });
      return docs;
    });
}

/**
 * Refreshes the cart drawer and every `[data-cart-count]` badge from a
 * fresh `header` section fetch — the one place this DOM-replacement
 * logic lives, called after any cart mutation from anywhere in the
 * theme (add-to-cart on the product page, quantity/remove inside the
 * drawer itself, quantity/remove on the full cart page).
 */
function gomezRefreshCartChrome() {
  return gomezFetchSections(['header']).then(function (docs) {
    var doc = docs.header;
    if (!doc) {
      return;
    }

    var freshCartDrawer = doc.getElementById('cart-drawer');
    var currentCartDrawer = document.getElementById('cart-drawer');
    if (freshCartDrawer && currentCartDrawer) {
      currentCartDrawer.replaceWith(freshCartDrawer);
    }

    var freshCounts = doc.querySelectorAll('[data-cart-count]');
    var currentCounts = document.querySelectorAll('[data-cart-count]');
    currentCounts.forEach(function (el, index) {
      if (freshCounts[index]) {
        el.replaceWith(freshCounts[index]);
      }
    });
  });
}

/**
 * POSTs a single line-item quantity change to Shopify's cart JSON
 * endpoint. Shared by the cart-drawer controller below and
 * assets/cart.js (the full cart page), so the request itself is
 * defined in exactly one place — each caller still does its own DOM
 * refresh afterward, since what needs refreshing differs by route.
 */
function gomezCartChangeItem(key, quantity) {
  return fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ id: key, quantity: quantity }),
  }).then(function (response) {
    if (!response.ok) {
      return response.json().then(function (data) {
        throw new Error((data && data.description) || 'Cart update failed');
      });
    }
    return response.json();
  });
}

/**
 * Dialog/drawer controller.
 *
 * HTML contract:
 *   - A trigger anywhere needs `data-dialog-open="{id}"`
 *   - The target is `<dialog id="{id}">`
 *   - Anything inside the dialog with `data-dialog-close` closes it
 *
 * Native <dialog> + showModal() already provides focus-move-in,
 * focus-trapping, and Escape-to-close. This controller only adds what
 * isn't native: wiring triggers by id, closing on backdrop click,
 * restoring focus to the trigger, and keeping only one dialog open at a
 * time.
 */
(function () {
  'use strict';

  var triggerForDialog = new WeakMap();
  var initializedDialogs = new WeakSet();

  function closeDialog(dialog) {
    if (dialog.open) {
      dialog.close();
    }
  }

  function closeAllExcept(exceptDialog) {
    document.querySelectorAll('dialog[open]').forEach(function (dialog) {
      if (dialog !== exceptDialog) {
        closeDialog(dialog);
      }
    });
  }

  function openDialog(dialog, trigger) {
    if (!dialog || dialog.open || typeof dialog.showModal !== 'function') {
      return;
    }
    closeAllExcept(dialog);
    triggerForDialog.set(dialog, trigger || document.activeElement);
    dialog.showModal();
  }

  // Attaches the backdrop-click and focus-restoration listeners to one
  // dialog. Guarded by `initializedDialogs` so it's safe to call more than
  // once for the same element — needed because the Theme Editor can
  // re-render a section (replacing its dialog with a new DOM node) after
  // this script has already run once on initial page load.
  function initDialog(dialog) {
    if (initializedDialogs.has(dialog)) {
      return;
    }
    initializedDialogs.add(dialog);

    // Clicking the ::backdrop area targets the <dialog> element itself
    // (its content lives in a child wrapper), so this only fires for
    // genuine backdrop clicks, not clicks inside the panel.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) {
        closeDialog(dialog);
      }
    });

    // Explicit focus restoration as a documented guarantee, on top of
    // (not instead of) native showModal()/close() focus handling.
    dialog.addEventListener('close', function () {
      var trigger = triggerForDialog.get(dialog);
      if (trigger && typeof trigger.focus === 'function') {
        trigger.focus();
      }
    });
  }

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    var openTrigger = event.target.closest('[data-dialog-open]');
    if (openTrigger) {
      var dialog = document.getElementById(openTrigger.getAttribute('data-dialog-open'));
      if (dialog && dialog.tagName === 'DIALOG') {
        openDialog(dialog, openTrigger);
      }
      return;
    }

    var closeTrigger = event.target.closest('[data-dialog-close]');
    if (closeTrigger) {
      var containingDialog = closeTrigger.closest('dialog');
      if (containingDialog) {
        closeDialog(containingDialog);
      }
    }
  });

  document.querySelectorAll('dialog').forEach(initDialog);

  // Theme Editor section reloads replace a section's DOM (including any
  // dialog inside it) without a full page load, so the querySelectorAll
  // pass above never sees the replacement node. This re-scans just the
  // reloaded section and initializes any dialog it finds — initDialog's
  // own guard makes this safe to run redundantly.
  document.addEventListener('shopify:section:load', function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }
    // A section's root element can itself be the <dialog> (not just a
    // container with one nested inside) — querySelectorAll below only
    // finds descendants, so the root has to be checked separately.
    if (event.target.tagName === 'DIALOG') {
      initDialog(event.target);
    }
    event.target.querySelectorAll('dialog').forEach(initDialog);
  });
})();

/**
 * Desktop navigation dropdown controller.
 *
 * HTML contract: any `<details data-nav-dropdown>` in the header.
 *
 * <details>/<summary> already opens/closes via click or keyboard with no
 * JavaScript. This only adds: closing when clicking outside, closing on
 * Escape, and closing sibling dropdowns when one opens.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-nav-dropdown]';

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }
    document.querySelectorAll(SELECTOR + '[open]').forEach(function (details) {
      if (!details.contains(event.target)) {
        details.removeAttribute('open');
      }
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') {
      return;
    }
    var openDetails = document.querySelector(SELECTOR + '[open]');
    if (openDetails) {
      openDetails.removeAttribute('open');
      var summary = openDetails.querySelector('summary');
      if (summary) {
        summary.focus();
      }
    }
  });

  // The native `toggle` event doesn't bubble, so this listens during the
  // capture phase to still observe it from a single document-level handler.
  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target;
      if (!(details instanceof Element) || !details.matches(SELECTOR) || !details.open) {
        return;
      }
      document.querySelectorAll(SELECTOR + '[open]').forEach(function (other) {
        if (other !== details) {
          other.removeAttribute('open');
        }
      });
    },
    true
  );
})();

/**
 * Sticky header "scrolled past top" state.
 *
 * HTML contract: `[data-sticky-header]` on the header element,
 * `[data-header-sentinel]` on a zero-height marker placed at the very
 * top of the page. Toggles `data-scrolled` on the header once the
 * sentinel scrolls out of view — used for the solid-background-after-
 * scroll behavior on a transparent/sticky header.
 */
(function () {
  'use strict';

  var header = document.querySelector('[data-sticky-header]');
  var sentinel = document.querySelector('[data-header-sentinel]');

  if (!header || !sentinel || typeof IntersectionObserver === 'undefined') {
    return;
  }

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      header.toggleAttribute('data-scrolled', !entry.isIntersecting);
    });
  });

  observer.observe(sentinel);
})();

/**
 * Announcement bar dismissal.
 *
 * HTML contract: `[data-announcement-bar]` on the bar,
 * `[data-announcement-dismiss]` on its dismiss button. Persists via
 * localStorage (see the small inline pre-paint check in
 * layout/theme.liquid that hides the bar again on the next page load
 * before first paint, avoiding a flash of a bar that should stay hidden).
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'gomez-fm:announcement-dismissed';

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }
    var dismissTrigger = event.target.closest('[data-announcement-dismiss]');
    if (!dismissTrigger) {
      return;
    }
    var bar = dismissTrigger.closest('[data-announcement-bar]');
    if (!bar) {
      return;
    }
    bar.setAttribute('hidden', '');
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch (error) {
      // Storage may be unavailable (private browsing, disabled storage).
      // The bar still dismisses for this page view; it just won't persist.
    }
  });
})();

/**
 * Quantity-stepper controller.
 *
 * HTML contract (snippets/quantity-selector.liquid): `[data-quantity-selector]`
 * wraps `[data-quantity-input]`, `[data-quantity-decrease]`, and
 * `[data-quantity-increase]`. Global rather than product-page-scoped —
 * the product form, the cart page, and the cart drawer all render this
 * same snippet.
 */
(function () {
  'use strict';

  document.querySelectorAll('[data-quantity-selector]').forEach(function (selector) {
    var input = selector.querySelector('[data-quantity-input]');
    var decrease = selector.querySelector('[data-quantity-decrease]');
    var increase = selector.querySelector('[data-quantity-increase]');
    if (!input) {
      return;
    }

    function setValue(next) {
      var parsedMin = parseInt(input.getAttribute('min'), 10);
      var min = isNaN(parsedMin) ? 1 : parsedMin;
      if (next < min) {
        next = min;
      }
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (decrease) {
      decrease.addEventListener('click', function () {
        setValue((parseInt(input.value, 10) || 0) - 1);
      });
    }
    if (increase) {
      increase.addEventListener('click', function () {
        setValue((parseInt(input.value, 10) || 0) + 1);
      });
    }
  });
})();

/**
 * Cart-drawer AJAX quantity/remove controller.
 *
 * HTML contract (see snippets/cart-drawer.liquid):
 *   - `[data-cart-drawer-form]` — the drawer's cart form
 *   - `[data-cart-quantity-input]` with `data-cart-line-key="{key}"` on
 *     each line's quantity field — `min="0"` is already enforced natively
 *   - `[data-cart-remove]` with `data-cart-line-key="{key}"` on each
 *     line's remove control — a real link to `item.url_to_remove`, so it
 *     already works with JavaScript disabled
 *   - `[data-cart-drawer-status]` (role="status") and
 *     `[data-cart-drawer-errors]` (role="alert") — existing live regions
 *
 * Every change goes through gomezCartChangeItem() (a single line) or the
 * standard `/cart/update.js` endpoint (the "Update" button, all changed
 * lines at once), then gomezRefreshCartChrome() re-renders the whole
 * drawer from the server's own markup — no line-item pricing, discount,
 * or subtotal math happens here in JavaScript. Without `fetch`, every
 * control here is already a working native form field or link, so the
 * standard form submission / link navigation handles it instead.
 */
(function () {
  'use strict';

  if (typeof window.fetch !== 'function') {
    return;
  }

  function regionsFor(root) {
    return {
      status: root.querySelector('[data-cart-drawer-status]'),
      errors: root.querySelector('[data-cart-drawer-errors]'),
    };
  }

  function announce(root, message, isError) {
    var region = isError ? regionsFor(root).errors : regionsFor(root).status;
    if (region) {
      region.textContent = message || '';
    }
  }

  function setBusy(root, busy) {
    root.querySelectorAll('[data-cart-quantity-input], [data-cart-remove]').forEach(function (el) {
      if (el.tagName === 'INPUT') {
        el.disabled = busy;
      } else {
        el.setAttribute('aria-disabled', busy ? 'true' : 'false');
      }
    });
  }

  function afterChange(root) {
    return gomezRefreshCartChrome().then(function () {
      var freshRoot = document.getElementById('cart-drawer');
      if (freshRoot) {
        announce(freshRoot, gomezThemeStrings.cartUpdated || '', false);
      }
    }).catch(function () {
      setBusy(root, false);
      announce(root, gomezThemeStrings.cartUpdateError || '', true);
    });
  }

  var changeTimers = {};

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!(input instanceof Element) || !input.matches('[data-cart-quantity-input]')) {
      return;
    }
    var root = input.closest('#cart-drawer');
    if (!root) {
      return;
    }
    var key = input.getAttribute('data-cart-line-key');
    var quantity = parseInt(input.value, 10);
    if (!key || isNaN(quantity) || quantity < 0) {
      return;
    }
    clearTimeout(changeTimers[key]);
    changeTimers[key] = setTimeout(function () {
      setBusy(root, true);
      gomezCartChangeItem(key, quantity).then(function () {
        return afterChange(root);
      }).catch(function () {
        setBusy(root, false);
        announce(root, gomezThemeStrings.cartUpdateError || '', true);
      });
    }, 300);
  });

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) {
      return;
    }
    var removeTrigger = event.target.closest('[data-cart-remove]');
    if (!removeTrigger) {
      return;
    }
    var root = removeTrigger.closest('#cart-drawer');
    if (!root) {
      return;
    }
    var key = removeTrigger.getAttribute('data-cart-line-key');
    if (!key) {
      return;
    }
    event.preventDefault();
    setBusy(root, true);
    gomezCartChangeItem(key, 0).then(function () {
      return afterChange(root);
    }).catch(function () {
      setBusy(root, false);
      announce(root, gomezThemeStrings.cartUpdateError || '', true);
    });
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-cart-drawer-form]')) {
      return;
    }
    var root = form.closest('#cart-drawer');
    if (!root) {
      return;
    }
    event.preventDefault();
    setBusy(root, true);

    var updates = {};
    root.querySelectorAll('[data-cart-quantity-input]').forEach(function (input) {
      var key = input.getAttribute('data-cart-line-key');
      var quantity = parseInt(input.value, 10);
      if (key && !isNaN(quantity) && quantity >= 0) {
        updates[key] = quantity;
      }
    });

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Cart update failed');
        }
        return afterChange(root);
      })
      .catch(function () {
        setBusy(root, false);
        announce(root, gomezThemeStrings.cartUpdateError || '', true);
      });
  });
})();

/**
 * Predictive search.
 *
 * Progressively enhances the search-drawer's form with Shopify's native
 * `/search/suggest.json?section_id=predictive-search` endpoint (see
 * sections/predictive-search.liquid) — the returned HTML is entirely
 * server-rendered, so no result markup, pricing, or discount logic is
 * duplicated here. The form it enhances already performs a full,
 * working standard GET search on submit regardless — pressing Enter (or
 * `fetch` being unavailable) always reaches real results.
 *
 * HTML contract:
 *   - `[data-predictive-search-scope]` wraps one form + one results
 *     container
 *   - `[data-predictive-search-form]` — the form, containing
 *     `input[name="q"]`
 *   - `[data-predictive-search-results]` — empty container JS fills in;
 *     hidden whenever it has no content
 *   - Result links inside the fetched markup carry
 *     `data-predictive-search-option` for arrow-key navigation
 */
(function () {
  'use strict';

  if (typeof window.fetch !== 'function') {
    return;
  }

  document.querySelectorAll('[data-predictive-search-scope]').forEach(function (scope) {
    var form = scope.querySelector('[data-predictive-search-form]');
    var input = form && form.querySelector('input[name="q"]');
    var results = scope.querySelector('[data-predictive-search-results]');
    if (!form || !input || !results) {
      return;
    }

    var debounceTimer;
    var activeController;

    function closeResults() {
      results.innerHTML = '';
      results.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    }

    function fetchResults(term) {
      if (activeController) {
        activeController.abort();
      }
      activeController = typeof AbortController === 'function' ? new AbortController() : null;

      var url =
        '/search/suggest.json?q=' +
        encodeURIComponent(term) +
        '&resources[type]=product,page,article&section_id=predictive-search';

      fetch(url, activeController ? { signal: activeController.signal } : {})
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Predictive search failed');
          }
          return response.json();
        })
        .then(function (data) {
          var html = data && data.sections && data.sections['predictive-search'];
          if (!html) {
            closeResults();
            return;
          }
          results.innerHTML = html;
          var hasContent = results.textContent.trim().length > 0;
          results.hidden = !hasContent;
          input.setAttribute('aria-expanded', hasContent ? 'true' : 'false');
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') {
            return;
          }
          closeResults();
        });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(debounceTimer);
      if (term.length < 2) {
        closeResults();
        return;
      }
      debounceTimer = setTimeout(function () {
        fetchResults(term);
      }, 200);
    });

    scope.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeResults();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return;
      }
      var options = Array.prototype.slice.call(results.querySelectorAll('[data-predictive-search-option]'));
      if (!options.length) {
        return;
      }
      event.preventDefault();
      var currentIndex = options.indexOf(document.activeElement);
      var nextIndex;
      if (event.key === 'ArrowDown') {
        nextIndex = currentIndex + 1 >= options.length ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
      }
      options[nextIndex].focus();
    });

    scope.addEventListener('focusout', function (event) {
      if (!scope.contains(event.relatedTarget)) {
        closeResults();
      }
    });
  });
})();
